import { Save, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { GenerationSuggestion } from './streamGeneration';

interface SuggestionChipsProps {
  suggestions: GenerationSuggestion[];
  /** Sends the text as the doctor's next chat message. */
  onSend: (message: string) => void;
  /** Phase 2 (§4.4 C8): the doctor tapped "💾 Always do this" — saves `statement` as an active preference. Omit to hide 'remember' chips entirely (a screen that hasn't wired memory shouldn't offer to save one). */
  onRemember?: (suggestion: Extract<GenerationSuggestion, { type: 'remember' }>) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * The AI's follow-up suggestions (✨) — one-click "add" prompts for something
 * worth adding to the note, plus (Phase 2) a "💾 Always do this" chip when
 * the doctor's last message read like a lasting instruction. A clarifying
 * question the AI needs answered is asked directly in its chat message
 * instead (see the chat_reply capability in aiSuggestions.js) — the doctor
 * just types the answer in the input box, like an ordinary chat reply,
 * rather than picking from option buttons. A "question"-type suggestion
 * (older shape) is silently ignored here for the same reason. Every "add"
 * click is an ordinary chat turn, so it is logged like anything the doctor
 * types; a "remember" click never becomes a chat turn — it goes straight to
 * POST /api/me/ai-memory instead.
 */
export function SuggestionChips({ suggestions, onSend, onRemember, disabled, className }: SuggestionChipsProps) {
  const addChips = suggestions.filter((s) => s.type === 'add');
  const rememberChip = onRemember ? suggestions.find((s) => s.type === 'remember') : undefined;
  if (!addChips.length && !rememberChip) return null;
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {addChips.map((suggestion) => (
        <Button
          key={suggestion.label}
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => onSend(suggestion.prompt)}
        >
          <Sparkles className="h-3.5 w-3.5 text-teal" aria-hidden="true" />
          {suggestion.label}
        </Button>
      ))}
      {rememberChip && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => onRemember?.(rememberChip)}
        >
          <Save className="h-3.5 w-3.5 text-teal" aria-hidden="true" />
          {rememberChip.label}
        </Button>
      )}
    </div>
  );
}
